// SPDX-License-Identifier: Apache-2.0
import GirafeHTMLElement from '../../base/GirafeHTMLElement';
import LayerWms from '../../models/layers/layerwms';
import { LayerAttribute } from '../../models/serverwfs';
import WfsFilterCondition from '../../tools/wfs/wfsfiltercondition';
import WfsFilter from '../../tools/wfs/wfsfilter';
import { isDate } from '../../models/xmlTypes';
import IGirafeContext from '../../tools/context/icontext';
import ColumnAliasHelper from '../../tools/utils/aliases';
import { FilterOperator, isTwoSidedFilterOperator, isValuelessFilterOperator } from '../../models/filter';

class QueryBuilderComponent extends GirafeHTMLElement {
  templateUrl = './template.html';
  styleUrls = ['../../styles/common.css', './style.css'];

  loading: boolean = true;
  deactivated: boolean = false;
  layer: LayerWms;
  layerAttributes: LayerAttribute[] = [];
  layerAttributesTranslations: Record<string, string> = {};
  currentLayerAttribute?: LayerAttribute = undefined;
  columnAliasHelper!: ColumnAliasHelper;

  showVal: boolean = false;
  showVal2: boolean = false;
  operatorOptions: { operator: FilterOperator; displayName: string }[] = [];

  public constructor(layer: LayerWms, context: IGirafeContext) {
    super('querybuilder', context);
    this.layer = layer;
  }

  override render() {
    if (!this.layer.ogcServer.urlWfs) {
      throw new Error('No WFS URL found. Please verify the Layer type.');
    }

    super.render();

    const queryLayers = this.layer.queryLayers?.split(',') ?? [];
    this.context.wfsManager.commonAttributesByLayer(this.layer).then((commonAttributeNames: LayerAttribute[]) => {
      if (queryLayers.length > 0) {
        // Save tranlations for each attribute
        for (const attribute of commonAttributeNames) {
          this.layerAttributesTranslations[attribute.name] = this.context.i18nManager.getTranslation(
            this.columnAliasHelper.getColumnAlias(queryLayers[0], attribute.name)
          );
        }
      }

      this.deactivated = commonAttributeNames.length === 0;
      if (this.deactivated) {
        console.log(
          `Filtering for layer group ${this.layer.name} is deactivated because the queryLayers don't have common attributes.`
        );
      }
      this.layerAttributes = commonAttributeNames;
      this.loading = false;

      super.refreshRender();
      this.initializeFilterFromLayer();
    });
  }

  get inputTypeForFilterValue(): string {
    if (isDate(this.currentLayerAttribute?.type || '')) {
      if (this.currentLayerAttribute?.type === 'datetime') {
        return 'datetime-local';
      }
      return 'date';
    }
    return 'string';
  }

  private initializeFilterFromLayer() {
    const condition = this.layer.filter?.conditions[0];
    if (condition instanceof WfsFilterCondition) {
      const [attributeSelect, operatorSelect, val, val2] = this.getFilterElements();
      attributeSelect.value = condition.property;
      this.attributeChanged();
      operatorSelect.value = condition.operator;
      this.operatorChanged();

      val.value = condition.value;
      val2.value = condition.value2;
    }
  }

  updateOperatorOptions() {
    this.operatorOptions = this.currentLayerAttribute?.type
      ? WfsFilterCondition.supportedOperatorsByAttributeType(this.currentLayerAttribute.type)
      : [];

    const operatorSelect = this.shadow.getElementById('operator') as HTMLSelectElement;
    const currentOperator = <FilterOperator>operatorSelect?.value;

    if (!this.operatorOptions.map((op) => op.operator).includes(currentOperator)) {
      operatorSelect.value = '';
      this.operatorChanged();
    }
  }

  attributeChanged() {
    const attributeSelect = this.shadow.getElementById('attribute') as HTMLSelectElement;
    const layerAttribute = this.layerAttributes.find((attr) => attr.name == attributeSelect.value);
    if (!layerAttribute) {
      throw new Error('Why is this object null ? This should never happen...');
    }

    this.currentLayerAttribute = layerAttribute;
    this.updateOperatorOptions();
    super.refreshRender();
  }

  operatorChanged() {
    const operatorSelect = this.shadow.getElementById('operator') as HTMLSelectElement;
    this.showVal = !isValuelessFilterOperator(operatorSelect?.value as FilterOperator);
    this.showVal2 = isTwoSidedFilterOperator(operatorSelect?.value as FilterOperator);
    super.refreshRender();
  }

  onValueKeyPress(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.filter();
    }
  }

  getFilterElements() {
    const attributeSelect = this.shadow.getElementById('attribute') as HTMLSelectElement;
    const operatorSelect = this.shadow.getElementById('operator') as HTMLSelectElement;
    const valueInput = this.shadow.getElementById('val') as HTMLInputElement;
    const value2Input = this.shadow.getElementById('val2') as HTMLInputElement;
    return [attributeSelect, operatorSelect, valueInput, value2Input];
  }

  validateInput(val: string): string {
    if (val && isDate(this.currentLayerAttribute?.type || '')) {
      const date = new Date(val);
      if (isNaN(date.getTime())) {
        throw new Error('Invalid date value entered !');
      }
    }
    return val;
  }

  getFilter() {
    const [attributeSelect, operatorSelect, val, val2] = this.getFilterElements();

    const property = attributeSelect.value;
    const operator = operatorSelect.value as FilterOperator;
    const value = this.validateInput(val.value);
    const value2 = this.validateInput(val2.value);
    if (this.showVal2 && (!value || !value2)) {
      return;
    }
    return new WfsFilter([new WfsFilterCondition(property, operator, value, value2, this.currentLayerAttribute?.type)]);
  }

  filter() {
    this.layer.filter = this.getFilter();
  }

  removeFilter() {
    const [attributeSelect, operatorSelect, val, val2] = this.getFilterElements();

    this.layer.filter = undefined;
    attributeSelect.value = '';
    operatorSelect.value = '';
    val.value = '';
    val2.value = '';
    this.currentLayerAttribute = undefined;
    this.showVal = false;
    this.showVal2 = false;
    super.refreshRender();
  }

  protected override connectedCallback() {
    super.connectedCallback();
    this.columnAliasHelper = new ColumnAliasHelper(this.context.stateManager);
    this.render();
  }
}

export default QueryBuilderComponent;
